import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import DropdownForm from "../Form/DropdownForm";
import FormField from "../Form/FormField";
import TextareaForm from "../Form/TextareaForm";
import {
  addNewSocialMediaApi,
  getCourseDetails,
  getSocialMediaDataToUpdate,
  getSocialMediaDropdown,
  updateSocialMediaChannel,
} from "../../apis";
import { useParams } from "react-router-dom";
import RowLoader from "../UI/RowLoader";
import LoaderAnimationComponent from "../../assets/lottie/LoaderAnimation/LoaderAnimationComponent";

Modal.setAppElement("#root");

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Channel title is required"),
  social_id: Yup.string().required("Title is required"),
  link: Yup.string()
    .required("Channel Link is required")
    .url("Invalid URL format"),
  confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
});

const AddOrEditSocialMedia = ({
  isOpen,
  onClose,
  setCourseDetails,
  mode = "add",
  channelId,
}) => {
  const { courseId } = useParams();
  const [socialMediaData, setSocialMediaData] = useState([]);
  const [dataToUpdate, setDataToUpdate] = useState([]);
  const [getDataLoading, setGetDataLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return; // Only fetch when modal is open

    let isMounted = true;

    getSocialMediaDropdown()
      .then((response) => {
        if (isMounted) setSocialMediaData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching dropdown data:", error);
        Swal.fire("Error", "Failed to load social media options", "error");
      });

    return () => {
      isMounted = false; // Prevents updating state after unmount
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === "edit" && channelId) {
      setGetDataLoading(true);
      getSocialMediaDataToUpdate(courseId, channelId)
        .then((response) => {
          setDataToUpdate(response.data);
        })
        .catch((error) => {
          console.error("Error fetching data to update:", error);
          Swal.fire("Error", "Failed to load social media data", "error");
          onClose();
        })
        .finally(() => {
          setGetDataLoading(false);
        });
    }

    return () => {
      setDataToUpdate([]); // Reset state when component unmounts
    };
  }, [isOpen, mode, courseId, channelId, onClose]);

  const handleFormSubmit = async (data, { resetForm }) => {
    const handleData = { ...data };
    setIsLoading(true);
    try {
      delete handleData.confirm;
      if (mode === "edit") {
        if (
          JSON.stringify({ ...initialValues, confirm: true }) ===
          JSON.stringify(data)
        ) {
          Swal.fire(
            "No changes detected",
            "No changes made to the social media channel",
            "info"
          );
          return;
        } else {
          await updateSocialMediaChannel(courseId, channelId, handleData);
        }
      } else {
        await addNewSocialMediaApi(courseId, handleData);
      }
      Swal.fire(
        "Success",
        "Social media channel submitted successfully!",
        "success"
      );
      resetForm();
      onClose();
      getCourseDetails(courseId)
        .then((response) => {
          setCourseDetails(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching course details:", error);
          Swal.fire("Sorry!", "Failed to fetch course details", "error");
        });
    } catch (error) {
      console.error("API Error:", error);
      console.log(error.response.data.message);

      Swal.fire(
        "Sorry!",
        error.response.data.message?.join(" , ") ||
          "Error while submited social media channel",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues =
    mode === "edit"
      ? {
          name: dataToUpdate?.name || "",
          social_id: dataToUpdate?.social_id || "",
          link: dataToUpdate?.link || "",
          confirm: false,
        }
      : {
          name: "",
          social_id: "",
          link: "",
          confirm: false,
        };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Add Package"
      className="tw-bg-white tw-p-5 tw-rounded-lg tw-shadow-lg tw-w-96 tw-mx-auto"
      overlayClassName="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-justify-center tw-items-center"
    >
      <h2 className="tw-text-lg tw-font-bold">
        {mode === "add" ? "Add Social Media" : "Edit Social Media"}
      </h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form className="row tw-items-center tw-min-h-[400px]">
            {!(getDataLoading && mode === "edit") ? (
              <>
                {/* Dropdown for channel category */}
                <DropdownForm
                  handleChange={handleChange}
                  name="social_id"
                  dataToDrop={socialMediaData}
                  error={errors.social_id}
                  touched={touched.social_id}
                  label="Social Channel Type"
                  placeholder="Select Channel Type"
                  gridStyle="col-sm-6"
                  labelKey="platform_name"
                />

                {/* Channel name */}
                <FormField
                  name="name"
                  label="Social Channel Title"
                  placeholder="Social Channel Title"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.name}
                />

                {/* Textarea for Channel Link */}
                <TextareaForm
                  name="link"
                  placeholder="Channel Link"
                  touched={touched.link}
                  errors={errors.link}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  label="Channel Link"
                />

                {/* Checkbox to Confirm */}
                <div className="col-md-12 tw-flex tw-items-center tw-mt-4">
                  <input
                    type="checkbox"
                    name="confirm"
                    id="confirm"
                    checked={values.confirm}
                    onChange={handleChange}
                    className="tw-mr-2 tw-w-4"
                  />
                  <label
                    htmlFor="confirm"
                    className="tw-text-sm tw-cursor-pointer"
                  >
                    I confirm that the Channel data is correct.
                  </label>
                </div>
                {errors.confirm && touched.confirm && (
                  <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                    {errors.confirm}
                  </p>
                )}

                {/* Buttons */}
                <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="tw-bg-gray-300 tw-px-4 tw-py-2 tw-rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="tw-bg-primary btn tw-text-white !tw-w-[100px] hover:tw-bg-primary/90 hover:tw-text-white"
                  >
                    {isLoading ? (
                      <RowLoader paddingStyle="tw-py-0" />
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="tw-w-full">
                <LoaderAnimationComponent className="tw-max-w-[400px] tw-mx-auto" />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddOrEditSocialMedia;
